// Catalog.ID Client — Recovery Kit Action Plan
// ============================================================================
// The two recovery-kit ceremonies, as title-row/action descriptors, shared by
// every surface that offers them: the personal (or acting-as-organization) kit
// in the Settings pane and the organization's own kit in the org workspace's
// Recovery Kit pane (`planOrgRecoveryActions`).
//
// One plan, because both surfaces have to get the same three things right:
//   - the create button says CREATE or REISSUE depending on what is on record;
//   - the fire drill only exists when there is a kit to drill — offered with no
//     kit it could only fail;
//   - so does the reprint: with no kit on record there is no set to print from.
// Two hand-built button rows is exactly how those drift apart.
//
// The wording and the ceremonies themselves are the Recovery Kit Addendum's
// (§7 create/reissue, §14 fire drill); `recovery-kit-status.ts` is the matching
// shared status line.
//
// ROUND 9 (2026-09-10, registration-restructure §16.6): "Print sheets…" joins
// them, and LEADS. It is the everyday verb — a replacement for a custodian who
// lost theirs, a new custodian for an existing sheet type, and the distribution
// round after registration — and it exists so that Reissue, which orphans every
// sheet already handed out, stops being the only way to get paper.

import type { PaneActionDescriptor } from '@catalog-shell';
import type { RecoveryKitInfo } from '../api/account';

/** A ceremony already running: both buttons say so rather than starting a second. */
const BUSY_TITLE = 'A recovery-kit ceremony is already running.';

/**
 * Plan the recovery-kit actions for a surface.
 *
 * @param kit  the kit on record, or null/undefined when there is none (or the
 *             lookup has not settled — the offer is the same either way: create).
 * @param exec runs the named ceremony; the caller knows whose kit it is.
 */
export function planRecoveryKitActions(
    kit: RecoveryKitInfo | null | undefined,
    exec: (action: 'reprint' | 'create' | 'test') => void,
    opts?: { busy?: boolean },
): PaneActionDescriptor[] {
    const busy = opts?.busy === true;
    const common = {
        disabled: busy || undefined,
        title: busy ? BUSY_TITLE : undefined,
    };
    const actions: PaneActionDescriptor[] = [];
    if (kit) {
        actions.push({
            key: 'reprint',
            label: 'Print sheets…',
            variant: 'primary',
            ...common,
            execute: () => exec('reprint'),
        });
    }
    actions.push({
        key: 'create',
        label: kit ? 'Reissue Recovery Kit…' : 'Create Recovery Kit…',
        // The primary slot belongs to the safe verb once there is a kit: a
        // reissue orphans every sheet already in a custodian's hands.
        variant: kit ? undefined : 'primary',
        ...common,
        execute: () => exec('create'),
    });
    if (kit) {
        actions.push({
            key: 'test',
            label: 'Test Recovery Kit…',
            ...common,
            execute: () => exec('test'),
        });
    }
    return actions;
}
